import React, { ElementType } from 'react'

import Collapse from '@mui/material/Collapse'
import ListItem from '@mui/material/ListItem'
// import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import ListItemButton from '@mui/material/ListItemButton'
import { ListItemButtonProps } from '@mui/material/ListItemButton'
import Stack from '@mui/material/Stack'

import Icon from 'src/components/ui/icon'

import Translations from 'src/layouts/components/Translations'
import { CustomPalette } from 'src/theme/palette'
import { NavLink as RouterNavLink } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { usePermission, PermissionPath } from 'src/hooks/usePermission'
import { useLayoutContext } from 'src/layouts/VerticalLayout'
import useResponsive from 'src/hooks/useResponsive'

interface Props {
  title: string
  path?: string
  icon?: React.ReactNode
  disabled?: string
  deep?: number
  children?: React.ReactNode
  permission?: PermissionPath
  onClick?: () => void
  content?: () => React.JSX.Element
}

const MenuNavLink = styled(ListItemButton)<
  ListItemButtonProps & {
    component?: ElementType
    to: string
    target?: '_blank' | undefined
  }
>(({ theme }) => ({
  width: '100%',

  borderRadius: theme.shape.borderRadius,
  transition: 'padding-left .25s ease-in-out, padding-right .25s ease-in-out',
  color: theme.palette.common.white,
  fill: theme.palette.common.white,

  '&:hover': {
    backgroundColor: (theme.palette as CustomPalette).navigationMenu.hover,
  },
  '&.active': {
    color: theme.palette.success.main,
    fill: theme.palette.success.main,

    '&, &:hover': {
      // boxShadow: `0px 2px 6px ${hexToRGBA(theme.palette.secondary.main, 0.48)}`,
      background: (theme.palette as CustomPalette).navigationMenu.active,

      '&.Mui-focusVisible': {
        background: (theme.palette as CustomPalette).navigationMenu.active,
      },
    },
    '& .MuiTypography-root, & svg': {
      color: `navigationMenu.textColor !important`,
    },
  },
}))

export const NavItem = (props: Props) => {
  const {
    path,
    title,
    disabled,
    icon,
    children,
    content: Content,
    permission,
    onClick,
    deep = 0,
  } = props
  const isAccess = usePermission(permission)
  const { setNavHandle } = useLayoutContext()
  const { isMobile } = useResponsive()

  const [open, setOpen] = React.useState(false)
  const { pathname } = useLocation()
  const isNavLinkActive = pathname === path && path

  const handleOpenContent = () => {
    setOpen((prev) => !prev)
  }

  if (!isAccess) return <></>

  return (
    <ListItem
      disablePadding={!isMobile}
      className="nav-group"
      sx={{
        mt: 1,
        px: '0 !important',
        flexDirection: 'column',
        ...(isMobile
          ? {}
          : {
              '&:hover': {
                '.navItemCollapseIcon': {
                  opacity: 1,
                },
              },
            }),
      }}
    >
      <MenuNavLink
        component={path ? RouterNavLink : undefined}
        {...(disabled && { tabIndex: -1 })}
        className={isNavLinkActive ? 'active' : ''}
        to={path === undefined ? '/' : `${path}`}
        onClick={(e) => {
          if (onClick) {
            e.preventDefault()
            e.stopPropagation()

            onClick()
          }
          if (path === undefined) {
            e.preventDefault()
            e.stopPropagation()
            setOpen(!open)
          }

          if (isMobile && path) {
            setNavHandle?.()
          }
        }}
        sx={{
          py: 3,
          px: 2 + deep * 1.5,

          ...(disabled ? { pointerEvents: 'none' } : { cursor: 'pointer' }),
        }}
      >
        <Stack
          direction="row"
          justifyContent={'space-between'}
          sx={{ width: '100%', overflow: 'hidden' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{ minWidth: 47, height: 21 }}
          >
            {icon}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent={'space-between'}
            flex={1}
          >
            <Typography
              className={'padding opacity'}
              color="inherit"
              noWrap
              fontSize={12}
              fontWeight={400}
            >
              <Translations text={title} />
            </Typography>
            {(children || Content) && (
              <Stack
                className="opacity"
                px={1}
                sx={{
                  transform: open ? 'rotate(-180deg)' : 'rotate(0deg)',
                }}
                onClick={
                  path
                    ? (event) => {
                        event.stopPropagation()
                        event.preventDefault()
                        handleOpenContent()
                      }
                    : undefined
                }
              >
                <Icon fontSize="1.125rem" icon={'tabler:chevron-down'} />
              </Stack>
            )}
          </Stack>
        </Stack>
      </MenuNavLink>
      {(children || Content) && (
        <Stack className="height disappear" sx={{ width: '100%' }}>
          <Collapse
            component="ul"
            onClick={(e) => e.stopPropagation()}
            in={open}
            sx={{
              p: 0,
              width: '100%',
            }}
          >
            {open && Content && <Content />}

            {open && children}
          </Collapse>
        </Stack>
      )}
    </ListItem>
  )
}
